<script setup lang="ts">
import type { WelcomeInvitation } from "@/models"
import type { Wishlist } from "@/models/wishlist"
import { computed, ref } from "vue"
import { ConfirmModal, SaveToCalendar } from "@/components"
import { Icon, ReadMore } from "@/components/ui"
import { useModal, useTranslation } from "@/composables"
import { ExternalWishlistLinkModalKey } from "@/symbols"
import { WishlistMode } from "@/types/wishlist.interface"
import { formatEventVaultDateRange, isAmazonUrl } from "@/utils"

const props = defineProps<{
  invitation: WelcomeInvitation
  wishlist?: Wishlist
}>()

const { d, t } = useTranslation()

const { toggle: toggleExternalLinkModal } = useModal(ExternalWishlistLinkModalKey)

const brand = import.meta.env.VITE_APP_NAME ?? "GUESTPIX"

const pendingExternalUrl = ref<string | null>(null)

function isSafeHttpUrl(url: string) {
  try {
    const { protocol } = new URL(url.trim())
    return protocol === "http:" || protocol === "https:"
  }
  catch {
    return false
  }
}

const isCustomWishlist = computed(() => props.wishlist?.wishlistMode === WishlistMode.Custom)

const displayWishlistLinks = computed(() =>
  props.wishlist?.links.filter(link => link.url && isSafeHttpUrl(link.url)) ?? [],
)

const showWishlistLinks = computed(() => {
  const mode = props.wishlist?.wishlistMode

  return (mode === WishlistMode.Custom || mode === WishlistMode.Curated) && displayWishlistLinks.value.length > 0
})

const showAmazonAssociateDisclosure = computed(() => {
  if (!showWishlistLinks.value)
    return false

  if (props.wishlist?.wishlistMode === WishlistMode.Curated)
    return true

  return displayWishlistLinks.value.some(link => link.url && isAmazonUrl(link.url))
})

const wishlistSectionLabel = computed(() => {
  const count = displayWishlistLinks.value.length
  const labelKey = props.wishlist?.wishlistMode === WishlistMode.Curated
    ? "invitation_page.gift_guide_section_label"
    : "invitation_page.wishlist_section_label"

  return t(labelKey, { count }, count)
})

const wishlistLinkClass = "flex w-fit cursor-pointer items-center gap-1.5 text-sm font-semibold leading-snug text-secondary-card-text underline-offset-2 hover:underline"

function onCustomWishlistLinkClick(url?: string) {
  if (!url)
    return

  pendingExternalUrl.value = url
  toggleExternalLinkModal()
}

function onConfirmExternalLink() {
  if (pendingExternalUrl.value)
    window.open(pendingExternalUrl.value, "_blank", "noopener,noreferrer")

  pendingExternalUrl.value = null
  toggleExternalLinkModal()
}

function onCancelExternalLink() {
  pendingExternalUrl.value = null
}

const googleMapsPlaceUrl = computed<string | null>(() => {
  const location = props.invitation.location

  if (!location?.googleId)
    return null

  const baseUrl = "https://www.google.com/maps/search/?api=1"
  const encodedName = encodeURIComponent(location?.name || location?.displayAddress || location?.formattedAddress || "")

  return `${baseUrl}&query=${encodedName}&query_place_id=${location.googleId}`
})

const eventDateDisplay = computed(() => {
  const eventDate = props.invitation.eventDate ?? new Date()

  return formatEventVaultDateRange(
    eventDate,
    props.invitation.eventEndDate,
    props.invitation.timezone,
    d,
  )
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <h1 v-if="invitation.title" data-test="invitation-title" class="text-2xl md:text-3xl font-heading">
      {{ invitation.title }}
    </h1>
    <div v-if="invitation.message" data-test="invitation-message" class="text-sm md:text-base">
      <ReadMore :lines="8" :content="invitation.message" />
    </div>
    <div class="flex flex-col gap-4 md:gap-5">
      <div v-if="showWishlistLinks" data-test="invitation-wishlist" class="flex gap-4">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary-card-text/8 text-secondary-card-text"
          aria-hidden="true"
        >
          <Icon icon="Gift" size="sm" />
        </div>
        <div class="flex min-w-0 flex-col gap-2">
          <span class="text-xs font-medium uppercase tracking-wider text-secondary-card-text/60">
            {{ wishlistSectionLabel }}
          </span>
          <ul class="flex flex-col gap-1.5">
            <li
              v-for="link in displayWishlistLinks"
              :key="link.url"
            >
              <a
                v-if="!isCustomWishlist"
                :href="link.url"
                data-test="invitation-wishlist-link"
                :class="wishlistLinkClass"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Icon icon="Link" class="shrink-0" size="xs" />
                <span class="break-all">{{ link.label || link.url }}</span>
              </a>
              <button
                v-else
                type="button"
                data-test="invitation-wishlist-link"
                :class="wishlistLinkClass"
                @click="onCustomWishlistLinkClick(link.url)"
              >
                <Icon icon="Link" class="shrink-0" size="xs" />
                <span class="break-all">{{ link.label || link.url }}</span>
              </button>
            </li>
          </ul>
          <p
            v-if="showAmazonAssociateDisclosure"
            class="text-xs text-secondary-card-text/60"
            data-test="invitation-amazon-associate-disclosure"
          >
            {{ t("wishlist_page.amazon_associate_disclosure", { brand }) }}
          </p>
        </div>
      </div>

      <div
        v-if="invitation.location?.displayAddress || invitation.location?.formattedAddress"
        data-test="invitation-location"
        class="flex gap-4"
      >
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary-card-text/8 text-secondary-card-text"
          aria-hidden="true"
        >
          <Icon icon="Location" size="sm" />
        </div>
        <div class="flex min-w-0 flex-col gap-1">
          <span class="text-xs font-medium uppercase tracking-wider text-secondary-card-text/60">
            {{ t("invitation_page.location") }}
          </span>
          <a
            v-if="googleMapsPlaceUrl"
            :href="googleMapsPlaceUrl"
            data-test="invitation-location-link"
            class="flex w-fit cursor-pointer items-center gap-1.5 text-sm font-semibold leading-snug underline-offset-2 hover:underline md:text-base"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{{ invitation.location.displayAddress || invitation.location.formattedAddress }}</span>
            <Icon icon="ArrowRight" class="shrink-0" size="xs" />
          </a>
          <p v-else class="md:text-base text-sm font-semibold leading-snug">
            {{ invitation.location.displayAddress || invitation.location.formattedAddress }}
          </p>
        </div>
      </div>
      <div data-test="invitation-date" class="flex gap-4">
        <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary-card-text/8 text-secondary-card-text" aria-hidden>
          <Icon icon="Calendar" size="sm" />
        </div>
        <div class="flex min-w-0 flex-col gap-1.5">
          <span class="text-xs font-medium uppercase tracking-wider text-secondary-card-text/60">
            {{ invitation.timezone ? t("invitation_page.date_time") : t("invitation_page.date") }}
          </span>
          <SaveToCalendar :invitation="invitation" align="end" full-width>
            <template #activator="{ isOpen }">
              <button
                type="button"
                class="flex w-full min-w-0 max-w-full cursor-pointer items-start gap-1.5 text-left text-sm font-semibold leading-snug underline-offset-2 hover:underline md:text-base"
                data-test="invitation-date-value"
                :aria-expanded="isOpen"
                aria-haspopup="menu"
              >
                <span class="min-w-0 flex-1 wrap-break-word">{{ eventDateDisplay }}</span>
                <Icon icon="CalendarPlus" class="mt-0.5 shrink-0" size="xs" />
              </button>
            </template>
          </SaveToCalendar>
        </div>
      </div>
    </div>
  </div>

  <ConfirmModal
    v-if="isCustomWishlist"
    :name="ExternalWishlistLinkModalKey"
    icon="ExternalLink"
    :title="t('invitation_page.external_wishlist_link.title', { brand })"
    :message="t('invitation_page.external_wishlist_link.message', { brand })"
    :confirm-text="t('invitation_page.external_wishlist_link.confirm_text')"
    :cancel-text="t('invitation_page.external_wishlist_link.cancel_text')"
    @confirm="onConfirmExternalLink"
    @cancel="onCancelExternalLink"
  />
</template>
